import { useAuth } from '@care-platform/auth';
import { Button, LoadingState, cn } from '@care-platform/ui';
import { LogOut } from 'lucide-react';
import { Suspense } from 'react';
import { NavLink, Outlet } from 'react-router';
import { useCopy } from '../copy';
import type { Pillar } from '../module';
import { useRouteRegistry } from '../routing/context';
import { useSession } from '../session';

const PILLARS: readonly Pillar[] = ['clinical', 'logistics', 'business'];

/** Sidebar navigation from the registry, grouped by pillar and filtered to what the user's permissions allow. */
export function Navigation() {
  const copy = useCopy();
  const { navigation, href } = useRouteRegistry();
  const session = useSession();
  const visible = navigation.filter((entry) => session.can(entry.permission));

  return (
    <nav aria-label={copy.shell.navigation} className="flex flex-col gap-4 p-3">
      {PILLARS.map((pillar) => {
        const entries = visible.filter((entry) => entry.pillar === pillar);
        if (entries.length === 0) return null;
        return (
          <div key={pillar}>
            <p className="px-2 pb-1 text-[0.68rem] font-bold tracking-[0.05em] text-muted-foreground uppercase">
              {copy.shell.groups[pillar]}
            </p>
            <ul className="flex flex-col gap-0.5">
              {entries.map((entry) => {
                const path = href(entry.routeId);
                if (!path) return null;
                const Icon = entry.icon;
                return (
                  <li key={entry.id}>
                    <NavLink
                      to={path}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2 rounded-sm px-2 py-1.5 text-[0.9rem] text-brand-navy hover:bg-slate-100',
                          isActive && 'bg-brand-tint font-semibold text-brand-blue hover:bg-brand-tint',
                        )
                      }
                    >
                      <Icon aria-hidden className="size-4" />
                      {entry.label(copy)}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

/** The shell frame: fixed header, pillar navigation on the left, the routed screen on the right. */
export function AppLayout() {
  const copy = useCopy();
  const auth = useAuth();
  const session = useSession();

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:p-2"
      >
        {copy.shell.skipToContent}
      </a>
      <header className="flex h-16 shrink-0 items-center gap-4 border-b border-border bg-white px-4">
        <div className="flex items-baseline gap-2">
          <span className="text-[1.1rem] font-bold text-brand-navy">{copy.product.owner}</span>
          <span className="text-[1.1rem] text-brand-blue">{copy.product.name}</span>
        </div>
        <span className="hidden h-6 w-px bg-border md:block" aria-hidden />
        <span className="hidden truncate text-sm text-muted-foreground md:block">
          {session.pharmacy.name}
        </span>
        <div className="ml-auto flex items-center gap-3">
          <span className="hidden text-sm text-muted-foreground sm:block">
            {copy.shell.signedInAs(session.user.displayName)}
          </span>
          <Button variant="outline" size="sm" onClick={() => void auth.signOut()}>
            <LogOut aria-hidden className="size-4" />
            {copy.shell.signOut}
          </Button>
        </div>
      </header>
      <div className="flex min-h-0 flex-1">
        <aside className="hidden w-60 shrink-0 border-r border-border bg-chrome md:block">
          <Navigation />
        </aside>
        <main id="main" className="min-w-0 flex-1 overflow-y-auto">
          <div className="border-b border-border bg-chrome md:hidden">
            <Navigation />
          </div>
          <Suspense fallback={<LoadingState label={copy.common.loading} className="m-4" />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
